import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Calendar, Download, Search, ChevronLeft, ChevronRight } from 'lucide-react';

const APT_TYPES = ['GP', 'ALLIED_HEALTH', 'THERAPY', 'FAMILY_MEETING', 'NDIS_REVIEW', 'CASE_CONFERENCE', 'TRANSPORT', 'OTHER'];
const STATUS_COLORS = { SCHEDULED: 'bg-blue-500 text-white', CONFIRMED: 'bg-green-500 text-white', COMPLETED: 'bg-slate-500 text-white', CANCELLED: 'bg-red-500 text-white' };

export default function AppointmentsPage() {
  const [apts, setApts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: '20' });
      if (typeFilter !== 'all') params.append('appointmentType', typeFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      const { data } = await api.get(`/appointments?${params}`);
      setApts(data.items || []);
      setTotal(data.total || 0);
    } catch { toast.error('Failed to load appointments'); }
    finally { setLoading(false); }
  }, [page, typeFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (id, action) => {
    try {
      if (action === 'cancel') await api.post(`/appointments/${id}/cancel`, { reason: 'Cancelled' });
      else await api.post(`/appointments/${id}/${action}`);
      toast.success(`Appointment ${action}ed`); load();
    } catch (e) { toast.error(e.response?.data?.error?.message || e.response?.data?.detail?.message || 'Failed'); }
  };

  const handleExport = async () => {
    try {
      const res = await api.get('/appointments/export-csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'appointments.csv'; a.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const handleCreate = async (form) => {
    try {
      await api.post('/appointments', form);
      toast.success('Appointment created'); setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.error?.message || e.response?.data?.detail?.message || 'Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="appointments-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Calendar className="w-6 h-6" />Appointments</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport} data-testid="export-csv-btn"><Download className="w-4 h-4 mr-2" />Export</Button>
          <Button onClick={() => setShowForm(true)} data-testid="new-appointment-btn"><Plus className="w-4 h-4 mr-2" />New</Button>
        </div>
      </div>

      <div className="flex gap-3 flex-wrap">
        <Select value={typeFilter} onValueChange={v => { setTypeFilter(v); setPage(1); }}>
          <SelectTrigger className="w-44" data-testid="type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Types</SelectItem>{APT_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-36" data-testid="status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="SCHEDULED">Scheduled</SelectItem><SelectItem value="CONFIRMED">Confirmed</SelectItem><SelectItem value="COMPLETED">Completed</SelectItem><SelectItem value="CANCELLED">Cancelled</SelectItem></SelectContent>
        </Select>
      </div>

      {loading ? <p className="text-muted-foreground">Loading...</p> : (
        <div className="space-y-2">
          {apts.map(a => (
            <Card key={a.id}>
              <CardContent className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <Badge className={STATUS_COLORS[a.status]}>{a.status}</Badge>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-muted-foreground">{a.appointmentNo}</span>
                      <Badge variant="outline">{a.appointmentType}</Badge>
                    </div>
                    <p className="font-medium truncate">{a.title}</p>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {a.participantName && <span className="mr-3">{a.participantName}</span>}
                      <span>{a.scheduledStart?.replace('T', ' ')}</span>
                      {a.location && <span className="ml-3">{a.location}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  {a.status === 'SCHEDULED' && <Button size="sm" variant="outline" onClick={() => handleAction(a.id, 'confirm')}>Confirm</Button>}
                  {(a.status === 'SCHEDULED' || a.status === 'CONFIRMED') && <Button size="sm" variant="outline" onClick={() => handleAction(a.id, 'complete')}>Complete</Button>}
                  {a.status !== 'COMPLETED' && a.status !== 'CANCELLED' && <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleAction(a.id, 'cancel')}>Cancel</Button>}
                </div>
              </CardContent>
            </Card>
          ))}
          {apts.length === 0 && <p className="text-center text-muted-foreground py-8">No appointments found</p>}
        </div>
      )}

      <div className="flex justify-between items-center">
        <span className="text-sm text-muted-foreground">{total} total</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm py-1">Page {page}</span>
          <Button size="sm" variant="outline" disabled={apts.length < 20} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New Appointment</DialogTitle></DialogHeader>
          <CreateAppointmentForm onSave={handleCreate} onCancel={() => setShowForm(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreateAppointmentForm({ onSave, onCancel }) {
  const [participants, setParticipants] = useState([]);
  const [form, setForm] = useState({
    participantId: '', title: '', appointmentType: 'GP', scheduledStart: '', scheduledEnd: '',
    location: '', providerName: '', transportRequired: false, staffRequired: false, notes: '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get('/participants?page=1&pageSize=50').then(r => setParticipants(r.data.items || r.data.data || [])).catch(() => {});
  }, []);

  return (
    <div className="space-y-3">
      <div><Label>Participant</Label>
        <Select value={form.participantId} onValueChange={v => set('participantId', v)}>
          <SelectTrigger data-testid="apt-participant"><SelectValue placeholder="Select participant" /></SelectTrigger>
          <SelectContent>{participants.map(p => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label>Title</Label><Input data-testid="apt-title" value={form.title} onChange={e => set('title', e.target.value)} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Type</Label>
          <Select value={form.appointmentType} onValueChange={v => set('appointmentType', v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{APT_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Provider</Label><Input value={form.providerName} onChange={e => set('providerName', e.target.value)} /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Start</Label><Input type="datetime-local" value={form.scheduledStart} onChange={e => set('scheduledStart', e.target.value)} /></div>
        <div><Label>End</Label><Input type="datetime-local" value={form.scheduledEnd} onChange={e => set('scheduledEnd', e.target.value)} /></div>
      </div>
      <div><Label>Location</Label><Input value={form.location} onChange={e => set('location', e.target.value)} /></div>
      <div className="flex gap-4">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.transportRequired} onChange={e => set('transportRequired', e.target.checked)} className="rounded" />Transport</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.staffRequired} onChange={e => set('staffRequired', e.target.checked)} className="rounded" />Staff Required</label>
      </div>
      <div><Label>Notes</Label><Input value={form.notes} onChange={e => set('notes', e.target.value)} /></div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button data-testid="create-apt-btn" onClick={() => onSave(form)} disabled={!form.participantId || !form.title || !form.scheduledStart}>Create</Button>
      </div>
    </div>
  );
}
